import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Link,
  useMediaQuery,
  useTheme,
  Stack,
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  PersonAdd as PersonAddIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  SentimentDissatisfied as SentimentDissatisfiedIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useAuthContext } from '../../../context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';

const AuthModal = ({ open, onClose, onSuccess, initialMode = 'login', forceFullScreen = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const authNavigate = useNavigate();
  const isFullScreen = forceFullScreen || isMobile;
  const [isLogin, setIsLogin] = useState(initialMode === 'login');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordStatus, setForgotPasswordStatus] = useState(null);
  const [formData, setFormData] = useState({
    fname: '',
    lname: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState('error');

  const { user, register, login, resumeGuestSession, requestPasswordReset, resendEmailVerification } = useAuthContext();

  const handleToggleMode = () => {
    setIsLogin(!isLogin);
    setErrors({});
    setAlertMessage('');
    setFormData({
      fname: '',
      lname: '',
      email: '',
      password: '',
      confirmPassword: '',
    });
  };

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (isLogin) {
      // Login validation
      if (!formData.email) newErrors.email = 'Email is required';
      if (!formData.password) newErrors.password = 'Password is required';
    } else {
      // Register validation
      if (!formData.fname) newErrors.fname = 'First name is required';
      if (!formData.email) newErrors.email = 'Email is required';
      if (!formData.password) newErrors.password = 'Password is required';
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    setAlertMessage('');

    try {
      let response;
      
      if (isLogin) {
        // Login
        response = await login(formData.email, formData.password);
      } else {
        // Register
        response = await register({
          fname: formData.fname,
          lname: formData.lname,
          email: formData.email,
          password: formData.password,
        });
      }

      if (response.success) {
        if (!isLogin) {
          // Registration - show verification message and switch to login
          setAlertMessage('Registration successful! Please check your email to verify your account.');
          setAlertSeverity('success');
          setTimeout(() => {
            setIsLogin(true);
            setAlertMessage('');
          }, 2000);
        } else {
          // Login - proceed normally
          onSuccess(response.user);
          onClose();
        }
      }
    } catch (error) {
      setAlertMessage(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGuestLogin = async () => {
    const redirectPath = location.state?.from || (location.pathname + location.search);

    onClose();

    if (user?.user_type === 'guest') {
      onSuccess?.(user);
      authNavigate(redirectPath, { replace: true });
      return;
    }

    try {
      const response = await resumeGuestSession();
      if (response.success && response.exists) {
        onSuccess?.(response.user);
        authNavigate(redirectPath, { replace: true });
      } else {
        authNavigate('/guest-setup', { state: { from: redirectPath } });
      }
    } catch {
      authNavigate('/guest-setup', { state: { from: redirectPath } });
    }
  };

  const handleGoogleLogin = () => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    // Use state.from when present (e.g. after guard redirect), else current path
    const redirectPath = location.state?.from || (location.pathname + location.search);
    const encodedRedirect = encodeURIComponent(redirectPath);
    window.location.href = `${apiUrl}/api/user-auth/auth/google?redirect=${encodedRedirect}`;
  };

  const handleForgotPasswordClick = () => {
    setShowForgotPassword(true);
    setForgotPasswordEmail('');
    setForgotPasswordStatus(null);
    setErrors({});
    setAlertMessage('');
  };

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
    setForgotPasswordEmail('');
    setForgotPasswordStatus(null);
    setErrors({});
    setAlertMessage('');
  };

  const handleForgotPasswordSubmit = async (event) => {
    event.preventDefault();
    
    if (!forgotPasswordEmail) {
      setErrors({ email: 'Email is required' });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(forgotPasswordEmail)) {
      setErrors({ email: 'Please enter a valid email address' });
      return;
    }

    setIsLoading(true);
    setErrors({});
    setForgotPasswordStatus(null);

    try {
      const response = await requestPasswordReset(forgotPasswordEmail);
      
      if (response.success) {
        setForgotPasswordStatus('success');
      }
    } catch (error) {
      // Check if error message indicates email doesn't exist
      if (error.message.toLowerCase().includes('not found') || 
          error.message.toLowerCase().includes('doesn\'t exist') ||
          error.message.toLowerCase().includes('no user')) {
        setForgotPasswordStatus('error');
      } else {
        setForgotPasswordStatus('error');
        setAlertMessage(error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!formData.email) {
      setAlertMessage('Please enter your email address first');
      setAlertSeverity('error');
      return;
    }

    setIsLoading(true);
    setAlertMessage('');

    try {
      const response = await resendEmailVerification(formData.email);
      
      if (response.success) {
        setAlertMessage('Verification email sent successfully! Please check your email.');
        setAlertSeverity('success');
      }
    } catch (error) {
      setAlertMessage(error.message);
      setAlertSeverity('error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setIsLogin(initialMode === 'login');
    setShowForgotPassword(false);
    setForgotPasswordEmail('');
    setForgotPasswordStatus(null);
    setFormData({
      fname: '',
      lname: '',
      email: '',
      password: '',
      confirmPassword: '',
    });
    setErrors({});
    setAlertMessage('');
    onClose();
  };

  // Update isLogin when initialMode changes
  React.useEffect(() => {
    if (open) {
      setIsLogin(initialMode === 'login');
    }
  }, [open, initialMode]);

  const mymPaperBg = forceFullScreen && isFullScreen
    ? [
        'radial-gradient(ellipse 80% 50% at 50% 0%, rgba(255,0,255,0.18) 0%, transparent 50%)',
        'radial-gradient(circle at 20% 80%, rgba(0,255,255,0.1) 0%, transparent 40%)',
        'radial-gradient(circle at 80% 20%, rgba(255,0,255,0.1) 0%, transparent 40%)',
        'linear-gradient(180deg, #0a0015 0%, #1a0033 50%, #0a0015 100%)',
      ].join(', ')
    : undefined;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isFullScreen}
      PaperProps={{
        sx: {
          bgcolor: isFullScreen && forceFullScreen ? 'transparent' : '#1A1A1A',
          background: mymPaperBg || (isFullScreen && forceFullScreen ? 'linear-gradient(180deg, #0a0015 0%, #1a0033 50%, #0a0015 100%)' : undefined),
          borderRadius: isFullScreen ? 0 : 2,
          m: isFullScreen ? 0 : 2,
          border: forceFullScreen && isFullScreen ? '1px solid rgba(255,0,255,0.2)' : undefined,
          boxShadow: forceFullScreen && isFullScreen ? '0 0 60px rgba(255,0,255,0.15), inset 0 0 80px rgba(0,0,0,0.3)' : undefined,
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
          pt: forceFullScreen ? 2 : 3,
          px: { xs: 2, sm: 3 },
          borderBottom: forceFullScreen ? '1px solid rgba(255,0,255,0.25)' : 'none',
          background: forceFullScreen ? 'linear-gradient(90deg, rgba(0,0,0,0.4) 0%, transparent 50%)' : 'transparent',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {forceFullScreen && (
            <Box
              component="img"
              src="/mind_your_music.png"
              alt="Mind Your Music"
              sx={{ height: { xs: 36, sm: 42 }, objectFit: 'contain' }}
            />
          )}
          {showForgotPassword && (
            <IconButton 
              onClick={handleBackToLogin} 
              size="small"
              sx={{ color: '#fff', mr: 0 }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography 
            variant={isMobile ? "h5" : "h4"} 
            component="h2"
            sx={{ 
              fontWeight: 700,
              color: '#fff',
              textShadow: forceFullScreen ? '0 0 20px rgba(255,0,255,0.4)' : 'none',
            }}
          >
            {showForgotPassword ? 'Reset Password' : (isLogin ? 'Sign In' : 'Sign Up')}
          </Typography>
        </Box>
        {forceFullScreen ? (
          <Button
            onClick={handleClose}
            startIcon={<ArrowBackIcon />}
            sx={{
              color: 'rgba(255,255,255,0.9)',
              textTransform: 'none',
              fontWeight: 600,
              border: '1px solid rgba(255,0,255,0.4)',
              borderRadius: '20px',
              px: 2,
              '&:hover': {
                borderColor: '#ff00ff',
                backgroundColor: 'rgba(255,0,255,0.15)',
                color: '#fff',
              },
            }}
          >
            Back to home
          </Button>
        ) : (
          <IconButton onClick={handleClose} size="small" sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent
        sx={{
          px: { xs: 2, sm: 3 },
          pb: 3,
          ...(forceFullScreen && isFullScreen && {
            maxWidth: 440,
            mx: 'auto',
            border: '1px solid rgba(255,0,255,0.15)',
            borderRadius: 2,
            mt: 2,
            backgroundColor: 'rgba(0,0,0,0.25)',
            boxShadow: '0 0 30px rgba(255,0,255,0.1)',
          }),
        }}
      >
        {showForgotPassword ? (
          // Forgot Password View
          <Box>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.7)',
                mb: 4,
                textAlign: 'center'
              }}
            >
              Enter your email address and we'll send you a link to reset your password.
            </Typography>

            {forgotPasswordStatus === 'success' ? (
              // Success State
              <Box sx={{ 
                textAlign: 'center',
                py: 4
              }}>
                <CheckCircleIcon 
                  sx={{ 
                    fontSize: 64, 
                    color: '#4CAF50',
                    mb: 2
                  }} 
                />
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: '#fff',
                    mb: 1,
                    fontWeight: 600
                  }}
                >
                  Check your email! 📧
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.7)',
                    mb: 3
                  }}
                >
                  We've sent a password reset link to <strong>{forgotPasswordEmail}</strong>
                </Typography>
                <Button
                  onClick={handleBackToLogin}
                  variant="outlined"
                  sx={{
                    borderColor: '#9C27B0',
                    color: '#9C27B0',
                    textTransform: 'none',
                    borderRadius: '8px',
                    px: 3,
                    '&:hover': {
                      borderColor: '#7B1FA2',
                      backgroundColor: 'rgba(156, 39, 176, 0.1)'
                    }
                  }}
                >
                  Back to Sign In
                </Button>
              </Box>
            ) : forgotPasswordStatus === 'error' ? (
              // Error State - Email doesn't exist
              <Box sx={{ 
                textAlign: 'center',
                py: 4
              }}>
                <SentimentDissatisfiedIcon 
                  sx={{ 
                    fontSize: 64, 
                    color: '#FF9800',
                    mb: 2
                  }} 
                />
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: '#fff',
                    mb: 1,
                    fontWeight: 600
                  }}
                >
                  Oops! Email not found 🎭
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.7)',
                    mb: 3
                  }}
                >
                  We couldn't find an account with that email address.<br />
                  Double-check the spelling or try a different email!
                </Typography>
                <Button
                  onClick={() => {
                    setForgotPasswordStatus(null);
                    setForgotPasswordEmail('');
                    setErrors({});
                  }}
                  variant="contained"
                  sx={{
                    bgcolor: '#9C27B0',
                    color: '#fff',
                    textTransform: 'none',
                    borderRadius: '8px',
                    px: 3,
                    '&:hover': {
                      bgcolor: '#7B1FA2',
                    }
                  }}
                >
                  Try Again
                </Button>
              </Box>
            ) : (
              // Form State
              <Box component="form" onSubmit={handleForgotPasswordSubmit}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={forgotPasswordEmail}
                  onChange={(e) => {
                    setForgotPasswordEmail(e.target.value);
                    if (errors.email) {
                      setErrors({});
                    }
                  }}
                  error={!!errors.email}
                  helperText={errors.email}
                  margin="normal"
                  autoFocus
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'rgba(255, 255, 255, 0.05)',
                      color: '#fff',
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#9C27B0',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#9C27B0',
                    },
                  }}
                  InputProps={{
                    startAdornment: <EmailIcon sx={{ mr: 1, color: 'rgba(255, 255, 255, 0.5)' }} />
                  }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={isLoading}
                  sx={{ 
                    py: 1.5,
                    mt: 3,
                    mb: 2,
                    bgcolor: '#9C27B0',
                    color: '#fff',
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: '#7B1FA2',
                    },
                    '&:disabled': {
                      bgcolor: 'rgba(156, 39, 176, 0.5)',
                    }
                  }}
                  startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                >
                  {isLoading ? 'Sending...' : 'Send Reset Link'}
                </Button>

                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  mt: 3
                }}>
                  <Button
                    onClick={handleBackToLogin}
                    sx={{
                      textTransform: 'none',
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&:hover': {
                        bgcolor: 'transparent',
                        color: '#9C27B0',
                      }
                    }}
                  >
                    ← Back to Sign In
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        ) : (
          // Regular Login/Signup View
          <>
            {alertMessage && (
          <Alert 
            severity={alertSeverity} 
            sx={{ mb: 2 }}
            action={
              alertMessage.includes('verify your email') && isLogin ? (
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleResendVerification}
                  disabled={isLoading}
                >
                  Resend
                </Button>
              ) : null
            }
          >
            {alertMessage}
          </Alert>
        )}

        {/* Google and Guest Login Buttons - First Section */}
        <Stack 
          direction={isMobile ? "column" : "row"} 
          spacing={2} 
          sx={{ mb: 3 }}
        >
          <Button
            onClick={handleGoogleLogin}
            variant="outlined"
            fullWidth
            disabled={isLoading}
            sx={{ 
              py: 1.5,
              borderColor: '#4285F4',
              color: '#4285F4',
              backgroundColor: 'transparent',
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '0.95rem',
              fontWeight: 500,
              '&:hover': {
                borderColor: '#357AE8',
                backgroundColor: 'rgba(66, 133, 244, 0.1)'
              }
            }}
            startIcon={
              <img 
                src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" 
                alt="Google"
                style={{ width: 20, height: 20 }}
              />
            }
          >
            Google
          </Button>

          <Button
            onClick={handleGuestLogin}
            variant="outlined"
            fullWidth
            disabled={isLoading}
            sx={{ 
              py: 1.5,
              borderColor: 'rgba(255, 255, 255, 0.3)',
              color: '#fff',
              backgroundColor: 'transparent',
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '0.95rem',
              fontWeight: 500,
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)'
              }
            }}
            startIcon={<PersonAddIcon />}
          >
            Guest
          </Button>
        </Stack>

        {/* Divider */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          my: 3,
          '&::before, &::after': {
            content: '""',
            flex: 1,
            height: '1px',
            bgcolor: 'rgba(255, 255, 255, 0.2)'
          }
        }}>
          <Typography 
            variant="body2" 
            sx={{ 
              px: 2, 
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '0.875rem'
            }}
          >
            or
          </Typography>
        </Box>

        {/* Email Registration Form */}
        <Box component="form" onSubmit={handleSubmit}>
          {!isLogin && (
            <>
              <TextField
                fullWidth
                label="First Name"
                value={formData.fname}
                onChange={handleInputChange('fname')}
                error={!!errors.fname}
                helperText={errors.fname}
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#9C27B0',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#9C27B0',
                  },
                }}
                InputProps={{
                  startAdornment: <PersonIcon sx={{ mr: 1, color: 'rgba(255, 255, 255, 0.5)' }} />
                }}
              />
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lname}
                onChange={handleInputChange('lname')}
                error={!!errors.lname}
                helperText={errors.lname}
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#9C27B0',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#9C27B0',
                  },
                }}
                InputProps={{
                  startAdornment: <PersonIcon sx={{ mr: 1, color: 'rgba(255, 255, 255, 0.5)' }} />
                }}
              />
            </>
          )}

          <TextField
            fullWidth
            label="Email"
            type="email"
            value={formData.email}
            onChange={handleInputChange('email')}
            error={!!errors.email}
            helperText={errors.email}
            margin="normal"
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'rgba(255, 255, 255, 0.05)',
                color: '#fff',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#9C27B0',
                },
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#9C27B0',
              },
            }}
            InputProps={{
              startAdornment: <EmailIcon sx={{ mr: 1, color: 'rgba(255, 255, 255, 0.5)' }} />
            }}
          />

          <TextField
            fullWidth
            label="Password"
            type="password"
            value={formData.password}
            onChange={handleInputChange('password')}
            error={!!errors.password}
            helperText={errors.password}
            margin="normal"
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'rgba(255, 255, 255, 0.05)',
                color: '#fff',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#9C27B0',
                },
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#9C27B0',
              },
            }}
            InputProps={{
              startAdornment: <LockIcon sx={{ mr: 1, color: 'rgba(255, 255, 255, 0.5)' }} />
            }}
          />

          {isLogin && (
            <Box sx={{ textAlign: 'right', mt: 1, mb: 2 }}>
              <Link
                component="button"
                variant="body2"
                onClick={handleForgotPasswordClick}
                sx={{ 
                  textDecoration: 'none',
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&:hover': {
                    color: '#9C27B0',
                  }
                }}
              >
                Forgot Password?
              </Link>
            </Box>
          )}

          {!isLogin && (
            <TextField
              fullWidth
              label="Confirm Password"
              type="password"
              value={formData.confirmPassword}
              onChange={handleInputChange('confirmPassword')}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              margin="normal"
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                  color: '#fff',
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#9C27B0',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#9C27B0',
                },
              }}
              InputProps={{
                startAdornment: <LockIcon sx={{ mr: 1, color: 'rgba(255, 255, 255, 0.5)' }} />
              }}
            />
          )}

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={isLoading}
            sx={{ 
              py: 1.5,
              mt: 3,
              mb: 2,
              bgcolor: forceFullScreen ? undefined : '#9C27B0',
              background: forceFullScreen ? 'linear-gradient(135deg, #ff00ff 0%, #00ffff 100%)' : undefined,
              color: '#fff',
              borderRadius: '12px',
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              boxShadow: forceFullScreen ? '0 4px 20px rgba(255,0,255,0.4)' : undefined,
              border: forceFullScreen ? '2px solid rgba(255,255,255,0.2)' : undefined,
              '&:hover': {
                bgcolor: forceFullScreen ? undefined : '#7B1FA2',
                background: forceFullScreen ? 'linear-gradient(135deg, #00ffff 0%, #ff00ff 100%)' : undefined,
                transform: forceFullScreen ? 'translateY(-2px)' : undefined,
                boxShadow: forceFullScreen ? '0 6px 28px rgba(255,0,255,0.5)' : undefined,
              },
              '&:disabled': {
                bgcolor: forceFullScreen ? 'rgba(80,80,80,0.5)' : 'rgba(156, 39, 176, 0.5)',
                background: forceFullScreen ? 'none' : undefined,
              }
            }}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              isLogin ? 'Sign In' : 'Sign Up'
            )}
          </Button>
        </Box>

        {/* Don't have account / Already have account */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          gap: 1,
          mt: 2
        }}>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
            {isLogin ? "Don't have an account?" : 'Already have an account?'}
          </Typography>
          <Button
            onClick={handleToggleMode}
            sx={{
              textTransform: 'none',
              color: '#9C27B0',
              fontWeight: 600,
              minWidth: 'auto',
              p: 0.5,
              '&:hover': {
                bgcolor: 'transparent',
                textDecoration: 'underline',
              }
            }}
          >
            {isLogin ? 'Sign up' : 'Sign in'}
          </Button>
        </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
